import { Box, Chip, Tab as JoyTab } from '@mui/joy';
import { Dispatch, SetStateAction } from 'react';

// By file: the core/elements barrel closes a require cycle through core/components (Header).
import MenuButton from 'core/elements/MenuButton';
import { ITab, ITabBadgeProps } from 'core/types';
import { Color, Size, Variant } from 'theme/enums';

interface IProps {
  tab: ITab;
  selectedSubTab: Record<string | number, string | number>;
  setSelectedSubTab: Dispatch<SetStateAction<Record<string | number, string | number>>>;
}

const MAX_BADGE_COUNT = 99;

const TabCountChip = ({ count, color = Color.PRIMARY, variant = Variant.SOFT }: ITabBadgeProps) =>
  count ? (
    <Chip
      data-testid="tabCountChip"
      size={Size.SM}
      color={color}
      variant={variant}
      sx={{ marginLeft: 1, fontWeight: 'md' }}
    >
      {count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : count}
    </Chip>
  ) : null;

export const Tab = ({ tab, selectedSubTab, setSelectedSubTab }: IProps) => {
  if (tab.hide) {
    return null;
  }

  if (tab?.subTabs) {
    return (
      <JoyTab value={tab.value} component={Box} sx={{ padding: 0 }}>
        <MenuButton
          label={tab.label}
          onClick={(e) => e.preventDefault()}
          menuItems={tab.subTabs.map((sub) => {
            const isSubTabSelected = selectedSubTab[tab.value] === sub.value;

            return {
              label: sub.label,
              selected: isSubTabSelected,
              color: isSubTabSelected ? Color.PRIMARY : undefined,
              onClick: () => setSelectedSubTab({ [tab.value]: sub.value }),
            };
          })}
          variant={Variant.PLAIN}
          size={Size.MD}
          sx={{
            color: 'inherit',
            backgroundColor: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            padding: '10px 16px',

            '&:hover': {
              color: 'inherit',
              backgroundColor: 'inherit',
            },
          }}
        />
      </JoyTab>
    );
  }

  return (
    <JoyTab
      value={tab.value}
      slotProps={{
        root: { onClick: () => setSelectedSubTab({}) },
      }}
    >
      {tab.label}

      {tab.badgeProps && <TabCountChip {...tab.badgeProps} />}
    </JoyTab>
  );
};
